import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export const StockChart = ({ data, isPositive = true }) => {
  // Process and reverse data for chronological order
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].reverse().map((item) => ({
      time: item.timestamp.split(" ")[1]?.slice(0, 5) || item.timestamp,
      price: item.close,
      open: item.open,
      high: item.high,
      low: item.low,
      volume: item.volume,
    }));
  }, [data]);

  const color = isPositive ? "#22C55E" : "#EF4444";
  const gradientId = `gradient-${isPositive ? "positive" : "negative"}`;

  if (chartData.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        No chart data available
      </div>
    );
  }

  // Calculate min/max for better Y-axis scaling
  const prices = chartData.map((d) => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const padding = (maxPrice - minPrice) * 0.1 || 1;

  return (
    <div className="chart-container h-[300px]" data-testid="stock-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#27272A"
            vertical={false}
          />
          <XAxis
            dataKey="time"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#A1A1AA", fontSize: 10 }}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis
            domain={[minPrice - padding, maxPrice + padding]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#A1A1AA", fontSize: 10 }}
            tickFormatter={(value) => `$${value.toFixed(0)}`}
            width={60}
          />
          <Tooltip
            content={<CustomTooltip isPositive={isPositive} />}
            cursor={{ stroke: "#3F3F46", strokeDasharray: "3 3" }}
          />
          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            animationDuration={750}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Custom tooltip component
function CustomTooltip({ active, payload, isPositive }) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const color = isPositive ? "text-green-500" : "text-red-500";

  return (
    <div className="glass rounded-sm p-3 border border-border">
      <p className="text-xs text-muted-foreground mb-1">{data.time}</p>
      <p className={`font-mono text-lg font-medium ${color}`} data-price>
        ${data.price.toFixed(2)}
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
        <span className="text-muted-foreground">Open</span>
        <span className="font-mono text-right">${data.open.toFixed(2)}</span>
        <span className="text-muted-foreground">High</span>
        <span className="font-mono text-right">${data.high.toFixed(2)}</span>
        <span className="text-muted-foreground">Low</span>
        <span className="font-mono text-right">${data.low.toFixed(2)}</span>
        <span className="text-muted-foreground">Volume</span>
        <span className="font-mono text-right">{data.volume.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default StockChart;
